import { Component, OnInit, OnDestroy, AfterViewInit, ViewChild, TemplateRef, inject } from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { FormsModule } from '@angular/forms';

import { EntityList } from '../../objects/entity-list';
import { LegalEntity } from '../../objects/legal-entity';

// Angular Material Imports
import { MatFormFieldModule } from '@angular/material/form-field'; 
import { MatButtonModule } from '@angular/material/button'; 
import { MatSelectModule } from '@angular/material/select';
import { MatGridListModule } from '@angular/material/grid-list';
import { MatDividerModule } from '@angular/material/divider';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatInputModule } from '@angular/material/input';

import { AgGridModule } from 'ag-grid-angular';

// Services
import { LegalEntityService } from '../../service/entity-services/legal-entity.service';
import { SidenavContentService } from '../../service/sidenav-content.service';
import { ViewsService } from '../../service/views.service';

// Column definitions
import * as legalEntityColumns from '../../../app/shared/entity-columns/legal-entity-columns';

import { Observable } from 'rxjs';

@Component({
  selector: 'app-legal-entities-list',
  templateUrl: './legal-entities-list.component.html',
  styleUrls: ['./legal-entities-list.component.css'],
  standalone: true,
  imports: [
    AsyncPipe,
    AgGridModule,
    MatButtonModule,
    MatFormFieldModule,
    MatSelectModule,
    MatInputModule,
    FormsModule,
    MatGridListModule,
    MatDividerModule,
    MatSidenavModule
  ]
})
export class LegalEntitiesListComponent extends EntityList implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild('legalEntitiesSidenavActions') legalEntitiesSidenavActions!: TemplateRef<any>;

  // Inject services using modern pattern
  private router = inject(Router);
  private sidenavContentService = inject(SidenavContentService);

  // Column definitions
  allColumns = legalEntityColumns.allColumns;
  
  defaultLegalEntityViewCols = [
    legalEntityColumns.colLegalEntityName,
    legalEntityColumns.colAddress,
    legalEntityColumns.colCity,
    legalEntityColumns.colState,
    legalEntityColumns.colZip,
    legalEntityColumns.colFein,
    legalEntityColumns.colComments
  ];

  columnSelectList = legalEntityColumns.columnSelectList;

  // Filter states
  showInactiveLegalEntities = false;
  filteredLegalEntities: LegalEntity[] = [];

  constructor() {
    super();
  }

  ngOnInit() {
    this.titleService.setTitle('Legal Entities');

    // Debug: Subscribe to allLegalEntities$ and log the data
    this.allLegalEntities$.subscribe(legalEntities => {
      // console.log('⚖️ Legal Entities data received:', legalEntities);
    });    
  }

  ngAfterViewInit(): void {
    // Send the template to the sidenav service
    this.sidenavContentService.updateSidenavContent(this.legalEntitiesSidenavActions);
  }

  ngOnDestroy(): void {
    // Clear the sidenav content when component is destroyed
    this.sidenavContentService.updateSidenavContent(null);
  }

  // Grid event handlers
  onCellClicked(event: any) {
    console.log('Legal Entity clicked:', event.data);
    console.log('Legal Entity number:', event.data.legalEntityNum);

    // Navigate to individual legal entity view
    this.router.navigate(['/securehome/legal-entity', event.data.legalEntityNum]);
  }

  onGridReady(params: any) {
    this.gridApi = params.api;
    this.columnApi = params.api;
  }

  // Navigation methods
  newLegalEntity() {
    console.log('Routing to new legal entity form');
    this.router.navigateByUrl('/securehome/new-legal-entity');
  }

  // Filter and search methods
  findTextInTableData(filterValue: string) {
    if (this.gridApi) {
      this.gridApi.setQuickFilter(filterValue);
    }
  }

  toggleInactiveLegalEntities() {
    this.showInactiveLegalEntities = !this.showInactiveLegalEntities;
    // You can implement filtering logic here if needed
    // this.filterLegalEntitiesByStatus();
  }

  // View management methods
  customizeColumns() {
    this.customizeColumnsUi = !this.customizeColumnsUi;
  }

  selectionChanged(event: any) {
    if (this.gridApi && this.columnApi) {
      this.gridApi.setColumnDefs(this.selectedCols);
      this.columnApi.setColumnState(this.selectedCols);
      this.fixColumnWidths();
    }
  }

  // View management methods (saveView, saveNamedViewNow, loadNamedView,
  // openSavedReportsDialog, openDeleteSavedViewsDialog, showSaveNamedView)
  // are inherited from EntityList base class

  // Export methods
  exportCSV() {
    if (this.gridApi) {
      this.gridApi.exportDataAsCsv({
        fileName: 'legal-entities-export.csv'
      });
    }
  }

  exportExcel() {
    if (this.gridApi) {
      this.gridApi.exportDataAsExcel({
        fileName: 'legal-entities-export.xlsx'
      });
    }
  }

  // Grid utility methods
  async fixColumnWidths() {
    const delay = (ms: number) => new Promise(res => setTimeout(res, ms));

    while (this.gridApi == undefined) {
      await delay(1000);
    }

    this.gridApi.sizeColumnsToFit();
  }
}